import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';

/**
 * Service for handling recommendation widget analytics and tracking data storage.
 *
 * This service manages the persistence of recommendation widget tracking data to localStorage,
 * enabling analytics and tracking of user interactions with recommendation widgets. It provides
 * customizable storage key generation and error handling that can be extended by Technical
 * Support teams for shop-specific requirements.
 *
 * @remarks
 * The service uses localStorage for persistence, which has browser-specific limitations:
 * - Storage quota varies by browser (typically 5-10MB)
 * - May be unavailable in private browsing mode
 * - Can be cleared by users at any time
 *
 * Technical Support teams can extend this service to:
 * - Customize storage key prefixes for multi-widget scenarios
 * - Implement alternative storage mechanisms (sessionStorage, IndexedDB)
 * - Add shop-specific error handling and fallback strategies
 * - Integrate with custom analytics platforms
 *
 * @example
 * Basic usage in widget code:
 * ```typescript
 * const analytic = new RecommendationAnalytic();
 * analytic.storeRecommendationWidgetTrackingData({
 *   wid: 'homepage-recs',
 *   rid: 'rec-12345',
 *   rt: 'similar-products'
 * });
 * ```
 *
 * @example
 * Extend to use custom storage prefix for multi-shop setup:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationAnalytic', (RecommendationAnalytic) => {
 *   return class CustomRecommendationAnalytic extends RecommendationAnalytic {
 *     protected getStorageKeyPrefix() {
 *       // Use shop domain in prefix to avoid conflicts
 *       return `${window.Shopify.shop}-boostSdRecommend`;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Extend to implement fallback storage strategy:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationAnalytic', (RecommendationAnalytic) => {
 *   return class CustomRecommendationAnalytic extends RecommendationAnalytic {
 *     protected handleStorageError(error, data) {
 *       // Try sessionStorage as fallback
 *       try {
 *         const key = this.generateStorageKey(data.wid);
 *         sessionStorage.setItem(key, JSON.stringify(data));
 *       } catch (fallbackError) {
 *         // Send to custom analytics endpoint as last resort
 *         this.sendToAnalyticsAPI(data);
 *       }
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class RecommendationAnalytic {
  constructor(protected appService: AppService) {}

  /**
   * Gets the storage key prefix for recommendation widget tracking data.
   *
   * This method returns the base prefix used when generating localStorage keys for
   * recommendation widgets. Override this method to customize the prefix for specific
   * shop requirements, such as multi-shop setups or namespacing strategies.
   *
   * @returns The storage key prefix string. Default is 'boostSdRecommend'.
   *
   * @remarks
   * The prefix is used as part of the full storage key format: `{prefix}-{widgetId}`.
   * Choose prefixes carefully to avoid conflicts with other localStorage keys.
   * Consider including shop identifiers for multi-shop implementations.
   *
   * @example
   * Override to include shop domain in prefix:
   * ```typescript
   * protected getStorageKeyPrefix(): string {
   *   const shopDomain = window.Shopify?.shop || 'default';
   *   return `${shopDomain}-boostSdRecommend`;
   * }
   * ```
   *
   * @example
   * Override to use environment-specific prefix:
   * ```typescript
   * protected getStorageKeyPrefix(): string {
   *   const env = process.env.NODE_ENV === 'production' ? '' : 'dev-';
   *   return `${env}boostSdRecommend`;
   * }
   * ```
   */
  protected getStorageKeyPrefix(): string {
    return 'boostSdRecommend';
  }

  /**
   * Generates a unique storage key for a recommendation widget.
   *
   * Combines the storage key prefix with the widget ID to create a unique localStorage
   * key for storing widget tracking data. This ensures each widget's tracking data is
   * stored separately and can be retrieved independently.
   *
   * @param widgetId - The unique identifier of the recommendation widget. Must be a
   * non-empty string that uniquely identifies the widget instance.
   *
   * @returns The generated storage key in the format `{prefix}-{widgetId}`.
   * For example: 'boostSdRecommend-homepage-recs'.
   *
   * @example
   * Generate storage key for a specific widget:
   * ```typescript
   * const key = analytic.generateStorageKey('product-page-similar');
   * // Returns: 'boostSdRecommend-product-page-similar'
   * ```
   */
  generateStorageKey(widgetId: string): string {
    return `${this.getStorageKeyPrefix()}-${widgetId}`;
  }

  /**
   * Handles errors that occur when storing recommendation tracking data to localStorage.
   *
   * This method is called when localStorage operations fail, providing an extension point
   * for custom error handling strategies. Common failure scenarios include storage quota
   * exceeded, private browsing mode restrictions, or localStorage being disabled.
   *
   * Override this method to implement shop-specific error handling, fallback storage
   * mechanisms, or integration with error tracking services.
   *
   * @param error - The error that occurred during the storage operation. Common error
   * types include QuotaExceededError (storage full) and SecurityError (private mode).
   *
   * @param data - The recommendation widget tracking data that failed to store. This
   * data can be used for retry attempts or alternative storage strategies.
   *
   * @remarks
   * Common localStorage errors and their causes:
   * - **QuotaExceededError**: Storage limit reached (typically 5-10MB per domain)
   * - **SecurityError**: localStorage disabled in private/incognito mode
   * - **InvalidStateError**: localStorage is being accessed before it's ready
   *
   * The default implementation logs errors through the application logger. In production environments,
   * consider implementing more robust error handling strategies.
   *
   * @example
   * Override to implement fallback to sessionStorage:
   * ```typescript
   * protected handleStorageError(error: Error, data: RecommendationWidgetTrackingData): void {
   *   console.warn('localStorage failed, trying sessionStorage:', error.message);
   *   try {
   *     const key = this.generateStorageKey(data.wid);
   *     sessionStorage.setItem(key, JSON.stringify(data));
   *   } catch (fallbackError) {
   *     console.error('All storage methods failed:', fallbackError);
   *   }
   * }
   * ```
   *
   * @example
   * Override to send failed data to analytics API:
   * ```typescript
   * protected handleStorageError(error: Error, data: RecommendationWidgetTrackingData): void {
   *   // Log error to monitoring service
   *   if (window.Sentry) {
   *     window.Sentry.captureException(error, {
   *       extra: { trackingData: data }
   *     });
   *   }
   *
   *   // Send data directly to analytics endpoint as fallback
   *   fetch('/api/track-recommendation', {
   *     method: 'POST',
   *     body: JSON.stringify(data),
   *     headers: { 'Content-Type': 'application/json' }
   *   }).catch(apiError => {
   *     console.error('Analytics API fallback failed:', apiError);
   *   });
   * }
   * ```
   */
  protected handleStorageError(error: Error, data: RecommendationWidgetTrackingData): void {
    this.appService.logger.error('Failed to store recommendation tracking data', {
      error,
      data,
    });
  }

  /**
   * Stores recommendation widget tracking data to localStorage.
   *
   * Persists tracking information for a recommendation widget to localStorage for later
   * retrieval and analytics processing. The data is serialized to JSON and stored under
   * a widget-specific key. If storage fails, the error is handled through the extensible
   * error handling mechanism.
   *
   * This method is lightweight and synchronous, with error handling that won't block
   * widget rendering if storage fails.
   *
   * @param data - The recommendation widget tracking data to persist. Must include a
   * valid widget ID (wid). Optional fields (rid, rt) can be included for additional
   * tracking context.
   *
   * @remarks
   * This method performs the following operations:
   * 1. Generates a unique storage key using the widget ID
   * 2. Serializes the tracking data to JSON
   * 3. Attempts to store in localStorage
   * 4. Delegates to error handler if storage fails
   *
   * The method is designed to be non-blocking - storage failures won't prevent
   * widget functionality from working. This ensures optimal widget performance
   * even when localStorage is unavailable.
   *
   * @example
   * Store basic tracking data for a recommendation widget:
   * ```typescript
   * analytic.storeRecommendationWidgetTrackingData({
   *   wid: 'homepage-recommendations',
   *   rid: 'rec-abc123',
   *   rt: 'frequently-bought-together'
   * });
   * ```
   *
   * @example
   * Store minimal tracking data (only widget ID required):
   * ```typescript
   * analytic.storeRecommendationWidgetTrackingData({
   *   wid: 'cart-page-upsells'
   * });
   * ```
   */
  storeRecommendationWidgetTrackingData(data: RecommendationWidgetTrackingData): void {
    try {
      const storageKey = this.generateStorageKey(data.wid);
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      this.handleStorageError(error as Error, data);
    }
  }
}

/**
 * Tracking data structure for recommendation widgets.
 *
 * Defines the shape of data stored for recommendation widget analytics and tracking.
 * This data is persisted to localStorage to track user interactions with recommendation
 * widgets and enable analytics reporting.
 *
 * @remarks
 * The structure is kept minimal for performance and storage efficiency. Only the widget
 * ID is required; recommendation ID and type are optional to support different tracking
 * scenarios without unnecessary data overhead.
 *
 * Technical Support teams can extend this type for shop-specific tracking needs, though
 * be mindful of localStorage size limitations when adding additional fields.
 *
 * @example
 * Basic tracking data with all fields:
 * ```typescript
 * const trackingData: RecommendationWidgetTrackingData = {
 *   wid: 'product-page-similar',
 *   rid: 'rec-xyz789',
 *   rt: 'similar-products'
 * };
 * ```
 *
 * @example
 * Minimal tracking data (widget ID only):
 * ```typescript
 * const trackingData: RecommendationWidgetTrackingData = {
 *   wid: 'homepage-bestsellers'
 * };
 * ```
 */
export type RecommendationWidgetTrackingData = {
  /**
   * Widget ID - The unique identifier of the recommendation widget.
   *
   * This is the only required field. It identifies which widget instance the tracking
   * data belongs to, enabling per-widget analytics and retrieval.
   *
   * @example 'homepage-recommendations', 'cart-page-upsells', 'product-similar-items'
   */
  wid: string;

  /**
   * Recommendation ID - The unique identifier for a specific recommendation session.
   *
   * Optional field used to track which specific recommendation set was shown to the user.
   * Useful for A/B testing and measuring recommendation effectiveness.
   *
   * @example 'rec-abc123', 'session-xyz789'
   */
  rid?: string;

  /**
   * Recommendation Type - The algorithm or strategy used to generate recommendations.
   *
   * Optional field indicating the type of recommendation logic applied. Enables
   * comparison of different recommendation strategies and their performance.
   *
   * @example 'similar-products', 'frequently-bought-together', 'trending', 'personalized'
   */
  rt?: string;
};
